import React, { useState, useCallback } from 'react';
import FlightRouteCard from '../flights/FlightRouteCard';
import FlightOptionsGrid from '../flights/FlightOptionsGrid';
import CuratedEscapes from '../flights/CuratedEscapes';
import MaterialIcon from '../ui/MaterialIcon';
import { SUB_SCREENS } from '../../hooks/useTabNavigation';
import { AIRPORTS_BY_IATA } from '../../data/airports';

const DEFAULT_ORIGIN = { code: 'JFK', city: 'New York', country: 'United States', name: 'John F Kennedy International Airport' };
const DEFAULT_DESTINATION = { code: 'DXB', city: 'Dubai', country: 'United Arab Emirates', name: 'Dubai International Airport' };

function getDefaultDepartureDate() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().split('T')[0];
}

function getDefaultReturnDate() {
  const d = new Date();
  d.setDate(d.getDate() + 21);
  return d.toISOString().split('T')[0];
}

function FlightsTab({ onSearchFlights, onNavigateToResults, cards }) {
  const [origin, setOrigin] = useState(DEFAULT_ORIGIN);
  const [destination, setDestination] = useState(DEFAULT_DESTINATION);
  const [departureDate, setDepartureDate] = useState(getDefaultDepartureDate);
  const [returnDate, setReturnDate] = useState(getDefaultReturnDate);
  const [cabin, setCabin] = useState('Economy');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [tripType, setTripType] = useState('round-trip');

  const handleSwapRoute = useCallback(() => {
    setOrigin((prev) => {
      setDestination(origin);
      return destination;
    });
  }, [origin, destination]);

  const handleSearch = useCallback(() => {
    const searchData = {
      origin: origin.code,
      originCity: origin.city,
      originCountry: origin.country || '',
      originData: origin,
      destination: destination.code,
      destinationCity: destination.city,
      destinationCountry: destination.country || '',
      destinationData: destination,
      departureDate,
      returnDate,
      cabin,
      adults,
      children,
      tripType,
    };

    if (onSearchFlights) {
      onSearchFlights(searchData);
    }

    if (onNavigateToResults) {
      onNavigateToResults(SUB_SCREENS.FLIGHT_RESULTS, searchData);
    }
  }, [origin, destination, departureDate, returnDate, cabin, adults, children, tripType, onSearchFlights, onNavigateToResults]);

  const handleDestinationClick = useCallback((dest) => {
    const airportData = AIRPORTS_BY_IATA.get(dest.code);
    setDestination({
      code: dest.code,
      city: dest.name,
      country: airportData?.country || dest.country || '',
      name: airportData?.name || '',
    });
  }, []);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="pt-2">
        <h1 className="text-2xl font-bold text-on-surface tracking-display">
          Where to <em className="not-italic italic text-primary">next</em>?
        </h1>
        <p className="text-sm text-on-surface-variant mt-1">
          Find the best flights & maximize your card rewards
        </p>
      </div>

      {/* Trip Type Toggle */}
      <div className="flex bg-surface-container-low rounded-xl p-1 gap-1">
        {['round-trip', 'one-way'].map((type) => (
          <button
            key={type}
            onClick={() => setTripType(type)}
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              tripType === type
                ? 'bg-white text-primary shadow-sm'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {type === 'round-trip' ? 'Round Trip' : 'One Way'}
          </button>
        ))}
      </div>

      {/* Route Card */}
      <FlightRouteCard
        origin={origin}
        destination={destination}
        onSwap={handleSwapRoute}
        onOriginChange={setOrigin}
        onDestinationChange={setDestination}
      />

      {/* Options Grid */}
      <FlightOptionsGrid
        departureDate={departureDate}
        returnDate={returnDate}
        cabin={cabin}
        adults={adults}
        children={children}
        hideReturn={tripType === 'one-way'}
        onDepartureDateChange={setDepartureDate}
        onReturnDateChange={setReturnDate}
        onCabinChange={setCabin}
        onAdultsChange={setAdults}
        onChildrenChange={setChildren}
      />

      {/* No Cards Prompt */}
      {(!cards || cards.length === 0) && (
        <div className="flex items-center gap-3 px-4 py-3 bg-primary-light/30 rounded-xl">
          <MaterialIcon name="credit_card" size={18} className="text-primary" />
          <p className="text-xs text-on-surface-variant">
            <span className="font-semibold text-on-surface">Add cards</span> to see smart savings on flights
          </p>
        </div>
      )}

      {/* Search CTA */}
      <button
        onClick={handleSearch}
        className="w-full py-4 rounded-2xl bg-gradient-to-r from-primary via-primary to-primary-dark text-on-primary font-semibold text-base shadow-card hover:shadow-card-hover transition-all ease-premium active:scale-[0.98] flex items-center justify-center gap-2"
      >
        <MaterialIcon name="flight_takeoff" size={20} className="text-on-primary" />
        Search Flights
      </button>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-outline-variant/15" />
        <span className="text-[11px] text-on-surface-variant/60 font-medium uppercase tracking-label">or explore</span>
        <div className="flex-1 h-px bg-outline-variant/15" />
      </div>

      {/* Curated Escapes */}
      <CuratedEscapes onDestinationClick={handleDestinationClick} />
    </div>
  );
}

export default FlightsTab;
